// Curated subset of Google Business Profile categories.
// Focus: highest-volume / most-confused / most-asked categories that operators
// and agencies look up. Easy to extend — add an entry and the route + sitemap
// pick it up automatically.

export interface GbpCategory {
  slug: string;
  name: string;
  /** Google's internal gcid (when known) — useful for API users. */
  gcid?: string;
  /** Parent industry bucket for filtering. */
  industry:
    | "Food & Drink"
    | "Retail"
    | "Health & Medical"
    | "Professional Services"
    | "Home Services"
    | "Automotive"
    | "Beauty & Wellness"
    | "Lodging"
    | "Education"
    | "Real Estate"
    | "Finance"
    | "Other";
  synonyms: string[];
  description: string;
  /** Which Google attribute groups this category typically unlocks. */
  unlocksAttributes: string[];
  /** Related categories operators often consider as the primary. */
  related: string[];
  /** Country availability flags (ISO 3166-1 alpha-2) or "global". */
  availability: "global" | string[];
  /** Real-world examples of who should pick this as primary. */
  bestFor: string;
  /** Common mistakes when choosing this category. */
  pitfall?: string;
}

export const GBP_CATEGORIES: GbpCategory[] = [
  {
    slug: "restaurant",
    name: "Restaurant",
    industry: "Food & Drink",
    synonyms: ["eatery", "dining", "diner"],
    description:
      "Sit-down or fast-casual establishment whose primary purpose is serving prepared meals to customers on-site. Broadest food category — almost always paired with a cuisine-specific secondary.",
    unlocksAttributes: [
      "Service options (Dine-in, Takeout, Delivery, Curbside)",
      "Dining options (Dessert, Seating)",
      "Highlights (Fast service, Casual)",
      "Offerings (Late-night food, Vegan options)",
      "Atmosphere",
      "Crowd",
      "Planning (Reservations recommended, Accepts reservations)",
      "Payments",
      "Accessibility",
    ],
    related: ["italian-restaurant", "fast-food-restaurant", "cafe", "bar-and-grill"],
    availability: "global",
    bestFor: "Full-service or fast-casual venues where guests order a meal on-premises.",
    pitfall:
      "Don't pick 'Restaurant' as the primary if a more specific cuisine category exists. The cuisine-specific category ranks better for cuisine searches.",
  },
  {
    slug: "italian-restaurant",
    name: "Italian Restaurant",
    industry: "Food & Drink",
    synonyms: ["trattoria", "ristorante"],
    description:
      "Restaurant whose menu is built around Italian cuisine — pasta, pizza, antipasti, regional Italian dishes. Use as primary for any venue marketing itself as Italian.",
    unlocksAttributes: ["All Restaurant attributes", "Serves wine", "Pizza takeout"],
    related: ["pizza-restaurant", "restaurant", "mediterranean-restaurant"],
    availability: "global",
    bestFor: "Trattorias, ristorantes, and Italian-American restaurants.",
  },
  {
    slug: "pizza-restaurant",
    name: "Pizza Restaurant",
    industry: "Food & Drink",
    synonyms: ["pizzeria"],
    description:
      "Restaurant whose primary offering is pizza, whether dine-in, takeout, or delivery. Distinct from Italian Restaurant — choose Pizza Restaurant when pizza is >50% of the menu.",
    unlocksAttributes: ["Delivery", "Takeout", "Curbside pickup", "No-contact delivery"],
    related: ["italian-restaurant", "fast-food-restaurant"],
    availability: "global",
    bestFor: "Pizzerias, slice shops, delivery-first pizza brands.",
  },
  {
    slug: "fast-food-restaurant",
    name: "Fast Food Restaurant",
    industry: "Food & Drink",
    synonyms: ["quick service restaurant", "QSR"],
    description:
      "Counter-service restaurant focused on speed, low price point, and standardised menu — typically with drive-through, takeout, and limited seating.",
    unlocksAttributes: ["Drive-through", "Takeout", "Late-night food", "Quick bite"],
    related: ["restaurant", "hamburger-restaurant", "chicken-restaurant"],
    availability: "global",
    bestFor: "National QSR chains, drive-throughs, counter-service brands.",
  },
  {
    slug: "cafe",
    name: "Cafe",
    industry: "Food & Drink",
    synonyms: ["coffee shop", "coffeehouse"],
    description:
      "Casual venue centred on coffee, tea, and light food — distinct from Coffee Shop in some markets, but Google treats them as overlapping. Use Coffee Shop if espresso drinks are the main draw and food is incidental.",
    unlocksAttributes: ["Good for working on a laptop", "Free Wi-Fi", "Outdoor seating"],
    related: ["coffee-shop", "bakery", "restaurant"],
    availability: "global",
    bestFor: "Independent cafes with food menus beyond pastries.",
    pitfall:
      "If you're a roaster-led espresso bar with minimal food, 'Coffee Shop' converts better for coffee-intent queries.",
  },
  {
    slug: "coffee-shop",
    name: "Coffee Shop",
    industry: "Food & Drink",
    synonyms: ["espresso bar", "coffee bar"],
    description:
      "Venue whose primary product is coffee — espresso drinks, drip, pour-over, and related beverages. Often paired with Cafe as secondary.",
    unlocksAttributes: ["Coffee", "Specialty coffee", "Outdoor seating", "Free Wi-Fi"],
    related: ["cafe", "bakery"],
    availability: "global",
    bestFor: "Specialty coffee bars, roaster cafes, espresso-first venues.",
  },
  {
    slug: "bar",
    name: "Bar",
    industry: "Food & Drink",
    synonyms: ["pub", "tavern"],
    description:
      "Venue whose primary purpose is the sale of alcoholic beverages for on-premises consumption. Food may be served but is secondary.",
    unlocksAttributes: [
      "Serves cocktails",
      "Serves beer",
      "Serves wine",
      "Live music",
      "Sports",
    ],
    related: ["pub", "cocktail-bar", "sports-bar", "wine-bar"],
    availability: "global",
    bestFor: "Cocktail bars, dive bars, neighbourhood pubs.",
  },
  {
    slug: "bakery",
    name: "Bakery",
    industry: "Food & Drink",
    synonyms: ["patisserie", "bread shop"],
    description: "Retail venue that produces and sells baked goods — bread, pastries, cakes.",
    unlocksAttributes: ["Takeout", "Cake", "Bread"],
    related: ["cafe", "dessert-shop", "patisserie"],
    availability: "global",
    bestFor: "Production bakeries with a retail counter.",
  },
  {
    slug: "grocery-store",
    name: "Grocery Store",
    industry: "Retail",
    synonyms: ["supermarket"],
    description: "Retail venue selling food and household staples for off-premises consumption.",
    unlocksAttributes: ["In-store shopping", "Delivery", "Curbside pickup", "SNAP/EBT (US)"],
    related: ["supermarket", "convenience-store", "specialty-food-store"],
    availability: "global",
    bestFor: "Independent grocers, neighbourhood supermarkets.",
  },
  {
    slug: "convenience-store",
    name: "Convenience Store",
    industry: "Retail",
    synonyms: ["c-store", "corner shop", "bodega"],
    description:
      "Small retail venue stocking everyday items with extended hours — often paired with a gas station as secondary.",
    unlocksAttributes: ["24-hour", "ATM on site"],
    related: ["grocery-store", "gas-station"],
    availability: "global",
    bestFor: "Standalone c-stores, attached forecourt shops.",
  },
  {
    slug: "clothing-store",
    name: "Clothing Store",
    industry: "Retail",
    synonyms: ["apparel store", "boutique"],
    description: "Retail venue selling clothing — broad category, refine with a secondary for women's/men's/children's.",
    unlocksAttributes: ["In-store shopping", "Curbside pickup", "Returns"],
    related: ["womens-clothing-store", "mens-clothing-store", "boutique"],
    availability: "global",
    bestFor: "Independent boutiques and multi-brand apparel shops.",
  },
  {
    slug: "hair-salon",
    name: "Hair Salon",
    industry: "Beauty & Wellness",
    synonyms: ["hairdresser", "salon"],
    description:
      "Service business offering haircuts, colouring, styling, and related treatments. One of the highest-volume local search categories.",
    unlocksAttributes: ["Appointments recommended", "Wheelchair accessible entrance", "LGBTQ+ friendly"],
    related: ["beauty-salon", "barber-shop", "nail-salon"],
    availability: "global",
    bestFor: "Salons offering cut, colour, and styling.",
    pitfall:
      "Don't choose Beauty Salon as primary if hair is your main service — Hair Salon ranks better for 'hair salon near me' queries.",
  },
  {
    slug: "barber-shop",
    name: "Barber Shop",
    industry: "Beauty & Wellness",
    synonyms: ["barber"],
    description: "Service business focused on men's haircuts, beard grooming, and shaves.",
    unlocksAttributes: ["Walk-ins welcome", "Appointments recommended"],
    related: ["hair-salon"],
    availability: "global",
    bestFor: "Traditional barber shops and modern men's grooming venues.",
  },
  {
    slug: "nail-salon",
    name: "Nail Salon",
    industry: "Beauty & Wellness",
    synonyms: ["nail bar"],
    description: "Service business offering manicures, pedicures, and nail artistry.",
    unlocksAttributes: ["Appointments recommended", "Gender-neutral restroom"],
    related: ["beauty-salon", "hair-salon"],
    availability: "global",
    bestFor: "Standalone nail bars and spa-style nail venues.",
  },
  {
    slug: "beauty-salon",
    name: "Beauty Salon",
    industry: "Beauty & Wellness",
    synonyms: ["beauty parlour"],
    description: "Broad beauty services category covering skin, lashes, brows, waxing, and treatments.",
    unlocksAttributes: ["Appointments recommended"],
    related: ["hair-salon", "nail-salon", "skin-care-clinic"],
    availability: "global",
    bestFor: "Multi-service beauty venues without a hair focus.",
  },
  {
    slug: "spa",
    name: "Spa",
    industry: "Beauty & Wellness",
    synonyms: ["day spa", "wellness spa"],
    description: "Wellness venue offering massage, body treatments, and relaxation services.",
    unlocksAttributes: ["Appointments recommended", "Gender-neutral restroom", "Restroom"],
    related: ["massage-therapist", "beauty-salon"],
    availability: "global",
    bestFor: "Day spas, hotel spas, wellness retreats.",
  },
  {
    slug: "gym",
    name: "Gym",
    industry: "Health & Medical",
    synonyms: ["fitness center", "health club"],
    description: "Membership-based fitness venue with equipment and/or classes.",
    unlocksAttributes: ["Wheelchair accessible entrance", "Gender-neutral restroom"],
    related: ["fitness-center", "personal-trainer", "yoga-studio"],
    availability: "global",
    bestFor: "Traditional gyms, boutique fitness clubs, 24-hour gyms.",
  },
  {
    slug: "yoga-studio",
    name: "Yoga Studio",
    industry: "Health & Medical",
    synonyms: ["yoga school"],
    description: "Studio dedicated to yoga classes and instruction.",
    unlocksAttributes: ["Appointments recommended"],
    related: ["gym", "pilates-studio"],
    availability: "global",
    bestFor: "Single-discipline yoga venues.",
  },
  {
    slug: "dental-clinic",
    name: "Dental Clinic",
    industry: "Health & Medical",
    synonyms: ["dentist", "dental practice"],
    description:
      "Healthcare facility providing dental services. Distinct from 'Dentist' (the practitioner) — most modern guidance is to use Dental Clinic for the location.",
    unlocksAttributes: ["Appointments recommended", "Wheelchair accessible entrance", "Wheelchair accessible restroom"],
    related: ["dentist", "orthodontist", "pediatric-dentist"],
    availability: "global",
    bestFor: "Multi-practitioner dental practices and clinics.",
  },
  {
    slug: "doctor",
    name: "Doctor",
    industry: "Health & Medical",
    synonyms: ["physician", "medical practitioner"],
    description: "Individual medical practitioner — use a specialty category if one fits better.",
    unlocksAttributes: ["Appointments recommended", "Telemedicine"],
    related: ["medical-clinic", "family-practice-physician", "general-practitioner"],
    availability: "global",
    bestFor: "Independent physicians without a more specific specialty.",
  },
  {
    slug: "medical-clinic",
    name: "Medical Clinic",
    industry: "Health & Medical",
    synonyms: ["clinic", "medical center"],
    description: "Outpatient healthcare facility with one or more practitioners.",
    unlocksAttributes: ["Appointments recommended", "Wheelchair accessible parking lot"],
    related: ["doctor", "urgent-care-center"],
    availability: "global",
    bestFor: "Multi-practitioner outpatient clinics.",
  },
  {
    slug: "chiropractor",
    name: "Chiropractor",
    industry: "Health & Medical",
    synonyms: ["chiropractic clinic"],
    description: "Practitioner specialising in musculoskeletal and spinal adjustments.",
    unlocksAttributes: ["Appointments recommended"],
    related: ["physical-therapist", "massage-therapist"],
    availability: "global",
    bestFor: "Independent chiropractic practices.",
  },
  {
    slug: "plumber",
    name: "Plumber",
    industry: "Home Services",
    synonyms: ["plumbing service", "plumbing contractor"],
    description:
      "Trade service installing and repairing water, drainage, and gas piping. Highly competitive map-pack vertical.",
    unlocksAttributes: ["Online estimates", "Onsite services", "Emergency service"],
    related: ["plumbing-contractor", "heating-contractor", "drainage-service"],
    availability: "global",
    bestFor: "Independent and franchise plumbing operators.",
    pitfall:
      "Service-area businesses must hide the address. Don't list a P.O. box or a home address you actually occupy — Google can suspend.",
  },
  {
    slug: "electrician",
    name: "Electrician",
    industry: "Home Services",
    synonyms: ["electrical contractor"],
    description: "Trade service installing and repairing electrical systems.",
    unlocksAttributes: ["Online estimates", "Onsite services", "Emergency service"],
    related: ["electrical-contractor", "handyman"],
    availability: "global",
    bestFor: "Independent electricians and small electrical firms.",
  },
  {
    slug: "hvac-contractor",
    name: "HVAC Contractor",
    industry: "Home Services",
    synonyms: ["heating contractor", "air conditioning contractor"],
    description: "Trade service installing and servicing heating, ventilation, and AC systems.",
    unlocksAttributes: ["Online estimates", "Emergency service"],
    related: ["heating-contractor", "air-conditioning-repair-service"],
    availability: "global",
    bestFor: "Full-service HVAC firms.",
  },
  {
    slug: "roofing-contractor",
    name: "Roofing Contractor",
    industry: "Home Services",
    synonyms: ["roofer", "roofing company"],
    description: "Trade service installing and repairing roofs — residential and commercial.",
    unlocksAttributes: ["Online estimates", "Onsite services"],
    related: ["general-contractor", "gutter-cleaning-service"],
    availability: "global",
    bestFor: "Roofing operators bidding storm-restoration work.",
  },
  {
    slug: "general-contractor",
    name: "General Contractor",
    industry: "Home Services",
    synonyms: ["builder", "contractor"],
    description: "Construction service managing whole projects — new builds, additions, remodels.",
    unlocksAttributes: ["Online estimates"],
    related: ["home-builder", "remodeler"],
    availability: "global",
    bestFor: "Firms taking lead-contractor responsibility on builds.",
  },
  {
    slug: "lawyer",
    name: "Lawyer",
    industry: "Professional Services",
    synonyms: ["attorney"],
    description: "Practitioner offering legal services — pair with a specialty secondary.",
    unlocksAttributes: ["Appointments recommended", "Online appointments"],
    related: ["personal-injury-attorney", "family-law-attorney", "criminal-justice-attorney"],
    availability: "global",
    bestFor: "Solo practitioners with mixed practice.",
    pitfall:
      "If you specialise (PI, family, criminal), choose the specialty primary — it converts better for high-intent queries.",
  },
  {
    slug: "personal-injury-attorney",
    name: "Personal Injury Attorney",
    industry: "Professional Services",
    synonyms: ["PI lawyer", "accident attorney"],
    description: "Lawyer specialising in personal injury cases — auto, premises, product liability.",
    unlocksAttributes: ["Appointments recommended", "Online appointments"],
    related: ["lawyer", "trial-attorney"],
    availability: "global",
    bestFor: "Firms whose case mix is >50% personal injury.",
  },
  {
    slug: "accountant",
    name: "Accountant",
    industry: "Professional Services",
    synonyms: ["CPA", "chartered accountant"],
    description: "Professional providing accounting, bookkeeping, and tax services.",
    unlocksAttributes: ["Online appointments", "Appointments recommended"],
    related: ["tax-preparation-service", "bookkeeping-service"],
    availability: "global",
    bestFor: "Independent CPAs and small accounting practices.",
  },
  {
    slug: "real-estate-agency",
    name: "Real Estate Agency",
    industry: "Real Estate",
    synonyms: ["realtor", "estate agent"],
    description: "Business representing buyers and sellers of property.",
    unlocksAttributes: ["Appointments recommended"],
    related: ["real-estate-consultant", "property-management-company"],
    availability: "global",
    bestFor: "Brokerages and independent agencies.",
  },
  {
    slug: "auto-repair-shop",
    name: "Auto Repair Shop",
    industry: "Automotive",
    synonyms: ["mechanic", "garage"],
    description: "Service business repairing motor vehicles.",
    unlocksAttributes: ["Appointments recommended", "Mechanic", "Oil change"],
    related: ["car-repair-and-maintenance-service", "tire-shop", "brake-shop"],
    availability: "global",
    bestFor: "Independent and chain mechanic shops.",
  },
  {
    slug: "car-dealer",
    name: "Car Dealer",
    industry: "Automotive",
    synonyms: ["car dealership", "auto dealer"],
    description: "Retailer selling new or used motor vehicles.",
    unlocksAttributes: ["In-store shopping", "Delivery"],
    related: ["used-car-dealer", "auto-broker"],
    availability: "global",
    bestFor: "Franchise dealerships and independent lots.",
  },
  {
    slug: "hotel",
    name: "Hotel",
    industry: "Lodging",
    synonyms: [],
    description:
      "Lodging providing rooms on a nightly basis, typically with reception and on-site services. Google Maps prioritises hotels with verified booking partners in the new ad-free Hotels tab.",
    unlocksAttributes: ["Free Wi-Fi", "Free parking", "Pool", "Air-conditioned"],
    related: ["resort-hotel", "motel", "bed-and-breakfast"],
    availability: "global",
    bestFor: "Branded and independent hotels.",
  },
  {
    slug: "bed-and-breakfast",
    name: "Bed & Breakfast",
    industry: "Lodging",
    synonyms: ["B&B", "guest house"],
    description: "Small lodging in a private home with breakfast included.",
    unlocksAttributes: ["Free breakfast", "Free Wi-Fi"],
    related: ["hotel", "inn"],
    availability: "global",
    bestFor: "Owner-operated guest houses.",
  },
  {
    slug: "veterinarian",
    name: "Veterinarian",
    industry: "Health & Medical",
    synonyms: ["vet", "animal hospital"],
    description: "Medical practitioner providing healthcare for animals.",
    unlocksAttributes: ["Appointments recommended", "Emergency service"],
    related: ["animal-hospital", "pet-groomer"],
    availability: "global",
    bestFor: "Small-animal practices.",
  },
  {
    slug: "preschool",
    name: "Preschool",
    industry: "Education",
    synonyms: ["nursery school", "kindergarten"],
    description: "Early-childhood education facility for children under school age.",
    unlocksAttributes: ["Wheelchair accessible entrance"],
    related: ["day-care-center", "elementary-school"],
    availability: "global",
    bestFor: "Standalone preschools.",
  },
  {
    slug: "day-care-center",
    name: "Day Care Center",
    industry: "Education",
    synonyms: ["childcare center", "daycare"],
    description: "Licensed facility providing daytime childcare for working parents.",
    unlocksAttributes: ["Wheelchair accessible parking lot"],
    related: ["preschool"],
    availability: "global",
    bestFor: "Licensed childcare centres.",
  },
  {
    slug: "bank",
    name: "Bank",
    industry: "Finance",
    synonyms: [],
    description: "Financial institution providing deposit, lending, and related services.",
    unlocksAttributes: ["ATM on site", "Drive-through"],
    related: ["credit-union", "atm"],
    availability: "global",
    bestFor: "Retail bank branches.",
  },
  {
    slug: "insurance-agency",
    name: "Insurance Agency",
    industry: "Finance",
    synonyms: ["insurance broker"],
    description: "Business selling and servicing insurance policies — auto, home, life, commercial.",
    unlocksAttributes: ["Online appointments", "Appointments recommended"],
    related: ["auto-insurance-agency", "life-insurance-agency"],
    availability: "global",
    bestFor: "Independent and captive agencies.",
  },
  {
    slug: "moving-company",
    name: "Moving Company",
    industry: "Home Services",
    synonyms: ["mover", "removalist"],
    description: "Service business moving residential or commercial belongings between locations.",
    unlocksAttributes: ["Online estimates"],
    related: ["self-storage-facility", "delivery-service"],
    availability: "global",
    bestFor: "Local and long-haul moving operators.",
  },
  {
    slug: "self-storage-facility",
    name: "Self-Storage Facility",
    industry: "Real Estate",
    synonyms: ["storage facility", "storage units"],
    description: "Rentable storage units accessed by the customer directly.",
    unlocksAttributes: ["24-hour access (where applicable)"],
    related: ["moving-company", "warehouse"],
    availability: "global",
    bestFor: "Self-storage operators.",
  },
  {
    slug: "marketing-agency",
    name: "Marketing Agency",
    industry: "Professional Services",
    synonyms: ["marketing consultant", "advertising agency"],
    description:
      "Agency providing marketing strategy and execution. Local SEO agencies typically pair this with a service-specific secondary.",
    unlocksAttributes: ["Online appointments", "Onsite services"],
    related: ["advertising-agency", "internet-marketing-service", "seo-agency"],
    availability: "global",
    bestFor: "Full-service marketing firms.",
  },
  {
    slug: "internet-marketing-service",
    name: "Internet Marketing Service",
    industry: "Professional Services",
    synonyms: ["digital marketing agency"],
    description: "Specialist provider of digital marketing services — SEO, paid media, content.",
    unlocksAttributes: ["Online appointments"],
    related: ["marketing-agency", "website-designer"],
    availability: "global",
    bestFor: "Digital-first agencies; closest official equivalent to 'SEO agency'.",
    pitfall:
      "There is no official 'SEO Agency' category in most regions — 'Internet Marketing Service' is the best primary for SEO-focused firms.",
  },
  {
    slug: "website-designer",
    name: "Website Designer",
    industry: "Professional Services",
    synonyms: ["web design company", "web developer"],
    description: "Service designing and building websites for clients.",
    unlocksAttributes: ["Online appointments"],
    related: ["internet-marketing-service", "graphic-designer"],
    availability: "global",
    bestFor: "Web design studios and freelance designers.",
  },
];

export function getCategoryBySlug(slug: string): GbpCategory | undefined {
  return GBP_CATEGORIES.find((c) => c.slug === slug);
}

export function listIndustries(): string[] {
  return Array.from(new Set(GBP_CATEGORIES.map((c) => c.industry))).sort();
}
